import { useEffect, useRef, useState } from 'react';
import type React from 'react';

import { Text } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

import { useI18n } from '@change-corgi/core/react/i18n';

import { HeaderImage } from 'src/app/pages/starterDashboardV2/shared/components';
import { Waypoint } from 'src/app/shared/components/scroll';

import { PageHeaderToolbar } from '../../../PageHeader/components/PageHeaderToolbar';
import { usePageHeader } from '../../../PageHeader/hooks/usePageHeader';

type InlinePageHeaderProps = {
	onVisibilityChange: (visible: boolean) => void;
};

export const InlinePageHeader: React.FC<InlinePageHeaderProps> = ({ onVisibilityChange }) => {
	const { translate } = useI18n();
	const {
		data: { actionButtons, imageUrl, isImageProcessing, title },
	} = usePageHeader();

	const textRef = useRef<HTMLHeadingElement>(null);
	const [isMultiLine, setIsMultiLine] = useState(false);

	useEffect(() => {
		const textElement = textRef.current;
		if (!textElement) return undefined;

		const getRenderedLineCount = (element: HTMLElement) => {
			const range = document.createRange();
			range.selectNodeContents(element);
			return range.getClientRects().length;
		};

		const checkLineCount = () => {
			const lineCount = getRenderedLineCount(textElement);
			setIsMultiLine(lineCount >= 3);
		};

		// Check initially
		checkLineCount();

		// Re-check on resize
		const resizeObserver = new ResizeObserver(checkLineCount);
		resizeObserver.observe(textElement);

		return () => {
			resizeObserver.disconnect();
		};
	}, [title]);

	return (
		<section
			className="border-stroke-weaker bg-background-base hidden w-full border-b px-12 py-8 lg:block"
			data-testid="inline-page-header"
			aria-label={translate('corgi.petition.starter_dashboard_v2.inline_page_header.aria_label')}
		>
			<Waypoint onEnter={() => onVisibilityChange(true)} onLeave={() => onVisibilityChange(false)} />
			<div
				data-testid="inline-page-header-content"
				className={cn('flex w-full flex-row justify-between gap-8', isMultiLine ? 'items-start' : 'items-center')}
			>
				<HeaderImage
					imageUrl={imageUrl}
					isImageProcessing={isImageProcessing}
					alt={title}
					imageClassName="rounded-xl h-36 w-36 gap-3.5"
					processingClassName="rounded-xl h-36 w-36"
					testIdPrefix="inline-header"
				/>
				<div
					data-testid="page-inline-header-title"
					className="flex w-full shrink-0 grow basis-0 flex-col items-start gap-5"
				>
					<Text
						data-testid="page-header-text"
						as="h1"
						truncate
						ref={textRef}
						className="color-[var(--typography-strong-color,#403A49)] text-size-heading-xl-sm line-clamp-3! whitespace-break-spaces!"
					>
						{title}
					</Text>
					{actionButtons.length > 0 && (
						<PageHeaderToolbar actionButtons={actionButtons} mobile={false} buttonVariant="inline" />
					)}
				</div>
			</div>
		</section>
	);
};
